import * as React from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ModalProps {
  open: boolean
  onClose: () => void
  title?: string
  description?: string
  children: React.ReactNode
  className?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  showHeader?: boolean
  hideDefaultClose?: boolean
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
  full: 'max-w-full mx-4',
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  className,
  size = 'md',
  showHeader = true,
  hideDefaultClose = false,
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          {...(!description ? { 'aria-describedby': undefined } : {})}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-full -translate-x-1/2 -translate-y-1/2',
            'rounded-lg bg-white p-6 shadow-xl',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            sizeClasses[size],
            className,
          )}
        >
          {showHeader ? (
            <div className={cn((title || description) && 'mb-4')}>
              <Dialog.Title className={cn(title ? 'text-lg font-semibold text-gray-900' : 'sr-only')}>
                {title ?? 'Dialog'}
              </Dialog.Title>
              {description ? (
                <Dialog.Description className="mt-1 text-sm text-gray-500">{description}</Dialog.Description>
              ) : null}
            </div>
          ) : (
            <Dialog.Title className="sr-only">{title ?? 'Dialog'}</Dialog.Title>
          )}
          {children}
          {!hideDefaultClose ? (
            <Dialog.Close
              className="absolute right-5 top-5 rounded-full p-1 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600 focus:outline-none focus:ring-2 focus:ring-stone-300 focus:ring-offset-2"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Dialog.Close>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
